import { Quaternion, Tools } from '@babylonjs/core';

import { RenderManager } from '../../manager';
import { DeviceOrientationBase, TouchData } from '../data';
import { ZEDCamera } from '../ZEDCamera';
import { toRadian } from '../ZEDCameraHelper';
import { InputCanvasProps } from '../ZEDCameraInputManager';

const alphaSensibility = 1;
const betaSensibility = 1;
const smoothFactor = 0;

let zedcamera: ZEDCamera | undefined;
let initAlpha = 0;

let alpha: number = 0;
let beta: number = 0;
let gamma: number = 0;

let screenOrientationAngle = 0;
let screenQuaternion: Quaternion = new Quaternion();
const constantTranform = new Quaternion(-Math.sqrt(0.5), 0, 0, Math.sqrt(0.5));

// 참고 : https://www.w3.org/TR/orientation-event/
const deviceOrientation = (evt: DeviceOrientationEvent) => {
  if (!zedcamera) return;

  if (smoothFactor) {
    alpha = evt.alpha !== null ? Tools.SmoothAngleChange(alpha, evt.alpha, smoothFactor) : 0;
    beta = evt.beta !== null ? Tools.SmoothAngleChange(beta, evt.beta, smoothFactor) : 0;
    gamma = evt.gamma !== null ? Tools.SmoothAngleChange(gamma, evt.gamma, smoothFactor) : 0;
  } else {
    alpha = evt.alpha !== null ? evt.alpha : 0;
    beta = evt.beta !== null ? evt.beta : 0;
    gamma = evt.gamma !== null ? evt.gamma : 0;
  }
  if (!beta && !gamma) return;

  let a = Quaternion.RotationYawPitchRoll(Tools.ToRadians(alpha), Tools.ToRadians(beta), -Tools.ToRadians(gamma));
  a = a.multiplyInPlace(screenQuaternion);
  a = a.multiplyInPlace(constantTranform);
  //Mirror on XY Plane
  a.z *= -1;
  a.w *= -1;
  const angle = a.toEulerAngles();
  zedcamera.alpha = initAlpha + -angle.y * alphaSensibility;
  zedcamera.beta = (90 * toRadian - angle.x) * betaSensibility;

  RenderManager.RenderUpdate();
};

const orientationChanged = () => {
  screenOrientationAngle =
    <any>window.orientation !== undefined
      ? +(<any>window.orientation)
      : (<any>window.screen).orientation && (<any>window.screen).orientation['angle']
      ? (<any>window.screen).orientation.angle
      : 0;
  screenOrientationAngle = -Tools.ToRadians(screenOrientationAngle / 2);
  screenQuaternion.copyFromFloats(0, Math.sin(screenOrientationAngle), 0, Math.cos(screenOrientationAngle));
};

const requestDeviceOrientationPermission = (window: Window) => {
  console.log('requestPermission : DeviceOrientation');

  const addListener = () => {
    window.addEventListener('orientationchange', orientationChanged);
    window.addEventListener('deviceorientation', deviceOrientation);
    //In certain cases, the attach control is called AFTER orientation was changed,
    //So this is needed.
    orientationChanged();
  };
  if (
    typeof DeviceOrientationEvent !== 'undefined' &&
    typeof (<any>DeviceOrientationEvent).requestPermission === 'function'
  ) {
    console.log('supported : DeviceOrientation');
    (<any>DeviceOrientationEvent)
      .requestPermission()
      .then((response: string) => {
        if (response === 'granted') {
          console.warn('Permission granted.');
          addListener();
        } else {
          console.warn('Permission not granted.');
        }
      })
      .catch((error: any) => {
        console.error(error);
      });
  } else {
    addListener();
  }
};

export const DeviceOrientationRotation: DeviceOrientationBase = {
  type: 'DeviceOrientation',
  touchCount: 1,
  key: 'DeviceOrientation',
  name: 'DeviceOrientationRotation',
  option: {},
  attachControl(
    camera: ZEDCamera,
    _canvasProps: InputCanvasProps,
    isMobileOrTablet: boolean,
    _noPreventDefault?: boolean | undefined
  ) {
    if (!isMobileOrTablet) return;

    zedcamera = camera;
    initAlpha = camera.alpha;

    orientationChanged();

    const hostWindow = camera.getScene().getEngine().getHostWindow();
    if (hostWindow) {
      requestDeviceOrientationPermission(hostWindow);
    } else {
      console.error('not exist hostWindow');
    }
  },
  detachControl(_camera: ZEDCamera, isMobileOrTablet: boolean) {
    alpha = 0;
    beta = 0;
    gamma = 0;

    if (!isMobileOrTablet) return;

    window.removeEventListener('orientationchange', orientationChanged);
    window.removeEventListener('deviceorientation', deviceOrientation);
  },
  checkInputs(_camera: ZEDCamera) {},
  reset() {},
  onTouchStart(_camera: ZEDCamera, _touchData: TouchData[], _canvasProps: InputCanvasProps) {},
  onTouchMoved(_camera: ZEDCamera, touchData: TouchData[], _canvasProps: InputCanvasProps) {
    const offset = touchData[0]!.movement;
    initAlpha -= offset.x / 100;
  },
  onTouchEnd(_camera: ZEDCamera, _touchData: TouchData[], _canvasProps: InputCanvasProps) {},
};
